import React, { useState, useEffect } from 'react';
import PropTypes                      from 'prop-types';
import Moment                         from 'moment';
import DateTimePicker                 from '~/components/forms/DateTimePicker';
import { TeammatePicker }             from '~/components/forms/team_member_picker';
import Tooltipable                    from '~/components/effects/tooltipable';
import { FontAwesomeIcon }            from '@fortawesome/react-fontawesome';
import VideoStore                     from '~/stores/video_store';
import VideoActions                   from '~/actions/video_actions';
import Select                         from '~/components/forms/select';

const processPickerValue = (value) => {
  if (!value || value.length === 0) return null;
  return value;
};

const VideoSearchFilters = ({
  searchData,
  clearSearch,
  hideFilters,
  handleSingleSelectChange,
  handleDateSelectChange,
  handleMultiSelectChange,
}) => {
  const uploaded_at_start = processPickerValue(searchData.uploaded_at_start);
  const uploaded_at_end = processPickerValue(searchData.uploaded_at_end);
  const [selectedEngagementType, setSelectedEngagementType] = useState(searchData.engagement_type || '');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const videoStoreListener = VideoStore.addListener(() => {
      const { categories, lastVideoStoreAction } = VideoStore.getState();

      if (lastVideoStoreAction === 'loadCategoriesDone') {
        setCategories(categories);
      }
    });

    VideoActions.loadCategories();

    return () => {
      videoStoreListener.remove();
    };
  }, []);


  const handleEngagementTypeChange = (option) => {
    const value = option?.value;
    setSelectedEngagementType(value);
    handleSingleSelectChange('engagement_type', value);
  };

  const handleEngagementRangeChange = (field, e) => {
    const value = e.target.value;
    handleSingleSelectChange(field, value);
  };

  const engagementTooltipText = (
    <ul className="pl-3 ml-2">
      <li className="text-left">
        Video email opens and clicks will be tracked for bulk, blast, and campaign emails sent through the Brokerkit email server. Video emails sent directly through your Google/Microsoft account via the contact profile cannot be tracked.
      </li>
      <li className="text-left mt-3">
        Video text clicks (views of your video) will be tracked for texts from numbers linked to a verified texting use case. Video text opens cannot be tracked and the video click tracking will only work in the US with a verified texting use case.
      </li>
    </ul>
  );

  const categoryTooltipText = (
    <ul className="pl-3 ml-2">
      {categories.map((category) => (
        <li key={category.id} className="text-left mt-2">
          {category.name} - {category.description}
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <div className="form-group mb15">
        <a href="#hide-filters" onClick={hideFilters}>Hide advanced options</a>
      </div>

      <div className="row">
        <div className="col-md-3 mb15">
          <div>
            <label htmlFor="favorites">Favorites</label>
            <Select
              id="favorites"
              name="favorites"
              placeholder="Select Favorites"
              value={processPickerValue(searchData.favorites)}
              onChange={(option) => handleSingleSelectChange('favorites', option?.value)}
              clearable
              options={[
                { value: 'all_favorites', label: 'All Favorites' },
                { value: 'my_favorites', label: 'My Favorites' },
              ]}
            />
          </div>
          <div className="mt-3">
            <label htmlFor="uploaded_at_start">Created</label>
            <div className="form-group mr5">
              <div className="mb-2">
                <DateTimePicker
                  id="uploaded_at_start"
                  placeholder="Start"
                  showClear
                  format="MMMM D, YYYY"
                  pickerType="date"
                  value={Moment(uploaded_at_start)?.format('MMMM D, YYYY')}
                  onChange={(date) => {
                    handleDateSelectChange('uploaded_at_start', date);
                  }}
                />
              </div>
              <div>
                <DateTimePicker
                  id="uploaded_at_end"
                  placeholder="End"
                  showClear
                  format="MMMM D, YYYY"
                  pickerType="date"
                  value={Moment(uploaded_at_end)?.format('MMMM D, YYYY')}
                  onChange={(date) => {
                    handleDateSelectChange('uploaded_at_end', date);
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3 mb15">
          <label htmlFor="video_owner_id">Owner</label>
          <TeammatePicker
            id="video_owner_id"
            name="owner_id"
            placeholder="Owner"
            value={processPickerValue(searchData.owner_id)}
            onChange={(selectedOptions) => {
              handleMultiSelectChange('owner_id', selectedOptions);
            }}
            multi
            blank
            clearable
            apiResource="/v1/videos/owner_options?only_existing=true"
          />
        </div>

        <div className="col-md-3 mb15">
          <label htmlFor="engagement_type">Engagement</label>
          <Tooltipable
            text={engagementTooltipText}
            trigger="click"
            className="ml-2"
            placement="right"
          >
            <FontAwesomeIcon
              icon={['fal', 'fa-info-circle']}
              size="xs"
              style={{ fontSize: '.6em' }}
              className="text-secondary" /
            >
          </Tooltipable>
          <Select
            id="engagement_type"
            name="engagement_type"
            placeholder="Select Engagement Type"
            value={selectedEngagementType}
            onChange={handleEngagementTypeChange}
            clearable
            options={[
              { value: 'opens_count', label: 'Opens' },
              { value: 'clicks_count', label: 'Clicks' },
            ]}
          />

          {selectedEngagementType && (
            <div className="row mt-2">
              <div className="col-6 pr-1">
                <input
                  type="number"
                  className="form-control"
                  placeholder="From"
                  value={searchData.engagement_from || ''}
                  onChange={(e) => handleEngagementRangeChange('engagement_from', e)}
                  min="0"
                />
              </div>
              <div className="col-6 pl-1">
                <input
                  type="number"
                  className="form-control"
                  placeholder="To"
                  value={searchData.engagement_to || ''}
                  onChange={(e) => handleEngagementRangeChange('engagement_to', e)}
                  min="0"
                />
              </div>
            </div>
          )}
        </div>

        <div className="col-md-3 mb15">
          <label htmlFor="video_category">Category</label>
          <Tooltipable
            text={categoryTooltipText}
            trigger="click"
            className="ml-2"
            placement="right"
          >
            <FontAwesomeIcon
              icon={['fal', 'fa-info-circle']}
              size="xs"
              style={{ fontSize: '.6em' }}
              className="text-secondary"
            />
          </Tooltipable>
          <Select
            id="video_category"
            name="category_id"
            placeholder="Select Category"
            value={processPickerValue(searchData.category_id)}
            onChange={(option) => handleSingleSelectChange('category_id', option?.value)}
            clearable
            options={categories.map((category) => ({
              value: category.id,
              label: category.name,
            }))}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-md-12">
          <button type="button" className="btn btn-sm btn-secondary" onClick={() => { clearSearch(); }}>
            Clear search
          </button>
        </div>
      </div>
    </div>
  );
};

VideoSearchFilters.defaultProps = {
  searchData:               {},
  clearSearch:              () => false,
  handleSingleSelectChange: () => false,
  handleDateSelectChange:   () => false,
  handleMultiSelectChange:  () => false,
};

VideoSearchFilters.propTypes = {
  searchData:               PropTypes.shape({}),
  clearSearch:              PropTypes.func,
  hideFilters:              PropTypes.func.isRequired,
  handleSingleSelectChange: PropTypes.func,
  handleDateSelectChange:   PropTypes.func,
  handleMultiSelectChange:  PropTypes.func,
};

export default VideoSearchFilters;
